import { Box, Flex, Text, List, ListItem, Link, useDisclosure, Heading } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { MouseEvent } from 'react';

import CATEGORIES from './Categories';
import CATEGORIESENGINEERS from './CategoriesEngineers';
import TYPES from './Types';
import TYPESENGINEERS from './TypesEngineers';

import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';

import Facebook from '../Icons/Facebook';
import Twitter from '../Icons/Twitter';
import LinkedIn from '../Icons/LinkedIn';
import sitePath from '../../helpers/sitePath';

function ActiveLink(props) {
  const router = useRouter();

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    router.push(props.href);
  };

  return (
    <Link
      href={props.href}
      onClick={handleClick}
      textDecoration="none"
      fontWeight={router.asPath === props.href ? '700' : '400'}
      {...props}
    >
      {props.children}
    </Link>
  );
}

function ActiveFilter(props) {
  const router = useRouter();

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (props.href === '?category=latest') {
      router.replace('', undefined);
    } else {
      router.push(props.href);
    }
  };

  let fontWeight = '400';

  if (props.href === `?category=${router.query.category}`) {
    fontWeight = '700';
  }

  if (props.href === `?category=latest` && !router.query.category) {
    fontWeight = '700';
  }

  return (
    <Link
      href={props.href}
      onClick={handleClick}
      fontWeight={fontWeight}
      textDecoration="none"
      {...props}
    >
      {props.children}
    </Link>
  );
}

export default function SidebarHeader({ type }: { type: string }) {
  const { isOpen, onToggle } = useDisclosure();

  const router = useRouter();
  const categoryQuery = router.query.category as string;
  const typeSlug = router.route;

  const categoriesUserType = type === 'engineers' ? CATEGORIESENGINEERS : CATEGORIES;

  const typesUserType = type === 'engineers' ? TYPESENGINEERS : TYPES;

  const matchedCategory = categoriesUserType.filter((cat) => cat.slug === categoryQuery)[0]?.label;

  const renderCategory = () => (matchedCategory ? matchedCategory : 'All');

  const matchedType = typesUserType.filter((type) => type.href === typeSlug)[0]?.label;

  return (
    <>
      <Box
        fontFamily="body"
        position={{ lg: 'sticky' }}
        top={{ lg: '1.875rem' }}
        pt={8}
        pr={8}
        pl={8}
        sx={{
          '& a': {
            fontSize: 'sm',
          },
        }}
      >
        <Box>
          <Box>
            <Link
              href={type === 'engineers' ? '/engineers/the-remote-engineer' : '/resources'}
              display="block"
              aria-label={`${
                type === 'engineers' ? '/engineers/the-remote-engineer' : '/resources'
              } Main Page`}
            >
              {type === 'engineers' ? (
                <Heading variant="heading-2" as="h1">
                  The Remote Engineer
                </Heading>
              ) : (
                <Heading variant="heading-2" as="h1">
                  The Remote Leader
                </Heading>
              )}
            </Link>
            <Text
              fontSize={{ base: '0.75rem', md: '0.875rem' }}
              mt={{ base: '1rem', md: '1.5rem' }}
              mb={{ base: '2rem', md: '2.375rem' }}
              color="text.secondary"
            >
              {type === 'engineers'
                ? 'Learn how to thrive in your role and make an impact in a remote-first world.'
                : 'Learn how to build thriving engineering teams in a remote-first world.'}
            </Text>
          </Box>
        </Box>
        <Box w="100%" height={{ base: '0.0625rem' }} bgColor="#D8D8D8" display={{ lg: 'none' }} />
        <Box
          display={{ base: 'flex', lg: 'block' }}
          p={{ lg: '0' }}
          mb={{ base: '4', lg: '0' }}
          position="relative"
        >
          <Box w={{ base: '50%', lg: 'auto' }}>
            <Flex
              pl={{ base: '2rem', lg: '0' }}
              alignItems="center"
              minH={{ base: '2.625rem', lg: '0' }}
            >
              <Box
                fontSize={{ base: '0.625rem', md: '0.6875rem' }}
                fontWeight="700"
                color="#AAAAAA"
                textTransform="uppercase"
                lineHeight="1"
              >
                Category
                <Box as="span" display={{ lg: 'none' }}>
                  :
                </Box>
              </Box>
              <Box
                fontSize={{ base: '0.75rem', md: '0.8125rem' }}
                fontWeight="700"
                display={{ lg: 'none' }}
                ml="0.375rem"
                lineHeight="1"
              >
                {renderCategory()}
              </Box>
            </Flex>
            <Box
              height={{
                base: isOpen ? '13.15rem' : '0',
                md: isOpen ? '13.1875rem' : '0',
                lg: 'auto',
              }}
              overflow="hidden"
              transition="height .3s ease-in-out"
              bgColor={{ base: '#F6F9FA', lg: 'transparent' }}
              position="relative"
            >
              <List
                p={{ base: '1rem 0 1rem 2.5rem', lg: '1rem 0 0' }}
                fontSize={{ base: '0.75rem', md: '0.8125rem' }}
                lineHeight={{ md: '1rem' }}
              >
                {categoriesUserType.map((item, i) => (
                  <ListItem key={item.label} mb="0.5rem">
                    <ActiveFilter href={`?category=${item.slug}`}>{item.label}</ActiveFilter>
                  </ListItem>
                ))}
              </List>
              <Box
                position="absolute"
                display={{ lg: 'none' }}
                right="0"
                top="0"
                bottom="0"
                margin="auto"
                w={{ base: '0.0625rem' }}
                height={{ base: '83%' }}
                bgColor="#D8D8D8"
              />
            </Box>
          </Box>
          <Box
            position="absolute"
            top="0.75rem"
            left="50%"
            display={{ lg: 'none' }}
            w={{ base: '0.0625rem' }}
            height={{ base: '0.9375rem' }}
            bgColor="#D8D8D8"
          />
          <Box
            w="8.875rem"
            height={{ base: '0.0625rem' }}
            bgColor="#D8D8D8"
            display={{ base: 'none', lg: 'block' }}
            mt={{ lg: '.5rem' }}
          />
          <Box w={{ base: '50%', lg: 'auto' }} position="relative">
            <Flex
              p={{ base: '0.75rem 2rem', lg: '1rem 0' }}
              alignItems="center"
              onClick={onToggle}
              tabIndex={0}
              w={{ lg: '8.875rem' }}
              minH={{ base: '2.625rem', md: '0' }}
            >
              <Box
                fontSize={{ base: '0.625rem', md: '0.6875rem' }}
                fontWeight="700"
                color="#AAAAAA"
                textTransform="uppercase"
                lineHeight="1"
              >
                Type
                <Box as="span" display={{ lg: 'none' }}>
                  :
                </Box>
              </Box>
              <Box
                fontSize={{ base: '0.75rem', md: '0.8125rem' }}
                fontWeight="700"
                display={{ lg: 'none' }}
                ml="0.375rem"
                lineHeight={{ lg: '1' }}
              >
                {matchedType}
              </Box>
            </Flex>
            <Box
              height={{
                base: isOpen ? '13.15rem' : '0',
                md: isOpen ? '13.1875rem' : '0',
                lg: '12.1875rem',
              }}
              overflow="hidden"
              transition={{ base: 'height .3s ease-in-out', lg: 'none' }}
              bgColor={{ base: '#F6F9FA', lg: 'transparent' }}
            >
              <List
                p={{ base: '1rem 0 1rem 2.5rem', lg: '0' }}
                fontSize={{ base: '0.75rem', md: '0.8125rem' }}
              >
                {typesUserType.map((item) => {
                  let catSlug = '';
                  if (
                    categoryQuery &&
                    categoriesUserType.map((cat) => cat.slug).includes(categoryQuery)
                  ) {
                    catSlug = `?category=${categoryQuery}`;
                  }
                  return (
                    <ListItem key={item.label} mb="0.5rem">
                      <ActiveLink href={`${item.href}${catSlug}`}>{item.label}</ActiveLink>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
            <Box
              display={{ lg: 'none' }}
              position="absolute"
              top={{ base: '.8125rem' }}
              right={{ base: '2rem', lg: '2.5rem' }}
              w="1rem"
              height="1rem"
              minW="none"
              p="0"
              transform={{ base: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
              transition="transform 0.3s ease-in-out"
              onClick={onToggle}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 15 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M1 1L7.5 6L14 1" stroke="#AAAAAA" strokeWidth="1.5" />
              </svg>
            </Box>
          </Box>
        </Box>
        {type === 'engineers' && (
          <Box pb="6" ml={{ base: '8', lg: '4.8125rem' }}>
            <Box
              fontSize={{ base: '0.625rem', md: '0.6875rem' }}
              fontWeight="700"
              color="#AAAAAA"
              textTransform="uppercase"
              lineHeight="1"
              mb={{ base: '0.75rem', lg: '4' }}
            >
              Share
            </Box>
            <Flex>
              <Box mr={{ base: '4' }}>
                <FacebookShareButton
                  url={sitePath(router.asPath)}
                  quote="Learn how to thrive in your role and make an impact in a remote-first world."
                >
                  <Facebook color="grey.900" />
                </FacebookShareButton>
              </Box>
              <Box mr={{ base: '4' }}>
                <TwitterShareButton
                  url={sitePath(router.asPath)}
                  title="Learn how to thrive in your role and make an impact in a remote-first world."
                >
                  <Twitter color="grey.900" />
                </TwitterShareButton>
              </Box>
              <Box>
                <LinkedinShareButton url={sitePath(router.asPath)}>
                  <LinkedIn color="grey.900" />
                </LinkedinShareButton>
              </Box>
            </Flex>
          </Box>
        )}
      </Box>
    </>
  );
}
